/**
 * Phase 101du (Feb 2026) — Rétribution partenaire hébergeur
 *
 * Page unifiée /partenaires/retribution avec 3 onglets :
 *   1. Fiches coûts machines (investissement + frais annexes)
 *   2. Contrats partenaires (palier CA, taux 40/50%, minimum garanti)
 *   3. Rapports mensuels calculés (moteur automatique depuis ventes Nayax)
 *
 * Le partenaire est un hébergeur pur : il touche 40% ou 50% du bénéfice net
 * (CA − matière − commissions − TVA − amortissement − annexes).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import {
  Calculator, Building2, Handshake, FileText, Plus, Edit3, Trash2,
  Lock, LockOpen, RefreshCw, Save, ArrowLeft, CheckCircle2, AlertCircle, Download, Mail, Pencil, Copy,
} from "lucide-react";
import { isStockDepot } from "../lib/stockDepot";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";
import { Link, useSearchParams } from "react-router-dom";
import ContractDocuments, { noticeDeadline } from "../components/partners/ContractDocuments";
import BatchNayaxComputeDialog from "../components/partners/BatchNayaxComputeDialog";

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const CHF = (v) => `${Number(v || 0).toFixed(2)} CHF`;

export default function PartnerCommission() {
  const [searchParams] = useSearchParams();
  const openContractId = searchParams.get("contract");
  const [tab, setTab] = useState(openContractId ? "contracts" : "costs");
  const [distributors, setDistributors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, pRes, cRes] = await Promise.all([
        api.get("/distributors"),
        api.get("/partners"),
        api.get("/partner-contracts?include_inactive=true"),
      ]);
      setDistributors((dRes.data || []).filter((d) => d.type === "AUTOMATIQUE" && !d.archived && !d.is_corporate && !isStockDepot(d)));
      setPartners((pRes.data || []).filter((p) => !p.archived));
      setContracts(cRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadReports = useCallback(async (year, month) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append("year", year);
      if (month) params.append("month", month);
      const { data } = await api.get(`/commission-reports?${params.toString()}`);
      setReports(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement rapports");
    }
  }, []);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="partner-commission-page">
      <div className="flex items-center gap-3">
        <Link to="/partenaires" className="text-[#575A57] hover:text-[#1F4E3D]" data-testid="pc-back">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] flex items-center gap-2">
            <Calculator className="w-6 h-6 text-[#1F4E3D]" />
            Rétribution partenaire hébergeur
          </h1>
          <p className="text-sm text-[#575A57]">
            Fiches coûts machines · contrats partenaires · rapports mensuels calculés automatiquement
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="pc-tabs">
          <TabsTrigger value="costs" data-testid="pc-tab-costs">
            <Building2 className="w-4 h-4 mr-1" /> Fiches coûts machines
          </TabsTrigger>
          <TabsTrigger value="contracts" data-testid="pc-tab-contracts">
            <Handshake className="w-4 h-4 mr-1" /> Contrats partenaires
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="pc-tab-reports">
            <FileText className="w-4 h-4 mr-1" /> Rapports mensuels
          </TabsTrigger>
        </TabsList>

        <TabsContent value="costs" className="mt-4">
          <MachineCostsTab
            distributors={distributors}
            loading={loading}
            onSaved={load}
          />
        </TabsContent>

        <TabsContent value="contracts" className="mt-4">
          <ContractsTab
            distributors={distributors}
            partners={partners}
            contracts={contracts}
            loading={loading}
            onChanged={load}
            confirm={confirm}
            openContractId={openContractId}
          />
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <ReportsTab
            distributors={distributors}
            partners={partners}
            contracts={contracts.filter((c) => c.active)}
            reports={reports}
            loadReports={loadReports}
            confirm={confirm}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ============== Tab 1 — Fiches coûts machines ==============

const NON_COPIED = ["id", "distributor_id", "created_at", "updated_at", "notes"];

function CopyCostsFrom({ distributors, onCopy }) {
  const [srcId, setSrcId] = useState("");
  const copy = async () => {
    try {
      const { data } = await api.get(`/machine-costs/${srcId}`);
      const vals = Object.fromEntries(Object.entries(data).filter(([k]) => !NON_COPIED.includes(k)));
      onCopy(vals);
      toast.success("Valeurs copiées : vérifiez puis enregistrez");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de copie");
    }
  };
  return (
    <div className="flex items-end gap-2 bg-[#F4F4F0] rounded-lg p-3" data-testid="cost-copy-from">
      <div className="flex-1">
        <Label className="text-xs">Copier les valeurs d&apos;une autre machine</Label>
        <Select value={srcId} onValueChange={setSrcId}>
          <SelectTrigger data-testid="cost-copy-source"><SelectValue placeholder="Choisir la machine modèle…" /></SelectTrigger>
          <SelectContent>
            {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" size="sm" disabled={!srcId} onClick={copy} data-testid="cost-copy-apply">
        <Copy className="w-3 h-3 mr-1" /> Copier
      </Button>
    </div>
  );
}

function MachineCostsTab({ distributors, loading, onSaved }) {
  const [openDist, setOpenDist] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);

  const openSheet = async (dist) => {
    setOpenDist(dist);
    try {
      const { data } = await api.get(`/machine-costs/${dist.id}`);
      setSheet(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement fiche");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/machine-costs/${openDist.id}`, sheet);
      toast.success(`Fiche coûts « ${openDist.name} » sauvegardée`);
      setOpenDist(null);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading && <div className="text-sm text-[#575A57]">Chargement…</div>}
      {!loading && distributors.length === 0 && (
        <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded p-3 text-sm text-[#8C6B2F]">
          Aucune machine AUTOMATIQUE configurée. Créez-en une dans /distributeurs.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {distributors.map((d) => (
          <Card key={d.id} className="hover:border-[#1F4E3D] transition-colors" data-testid={`pc-machine-${d.id}`}>
            <CardContent className="p-4">
              <div className="font-semibold text-[#1A1C1A]">{d.name}</div>
              <div className="text-xs text-[#575A57] mt-1">{d.code || "sans code"}</div>
              <Button
                onClick={() => openSheet(d)}
                variant="outline"
                size="sm"
                className="mt-3 w-full"
                data-testid={`pc-edit-costs-${d.id}`}
              >
                <Edit3 className="w-3 h-3 mr-1" /> Éditer la fiche coûts
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!openDist} onOpenChange={(o) => { if (!o) { setOpenDist(null); setSheet(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fiche coûts — {openDist?.name}</DialogTitle>
            <DialogDescription>
              Investissement machine amorti sur {sheet?.amort_months || 48} mois + frais annexes annuels
              mensualisés. Base du calcul de rétribution partenaire.
            </DialogDescription>
          </DialogHeader>

          {sheet && (
            <div className="space-y-5">
              <CopyCostsFrom
                distributors={distributors.filter((d) => d.id !== openDist?.id)}
                onCopy={(src) => setSheet({ ...sheet, ...src })}
              />
              <fieldset className="border border-[#E5E5E0] rounded-lg p-3">
                <legend className="text-xs font-semibold text-[#1F4E3D] px-2">Investissement initial (CHF HT)</legend>
                <div className="grid grid-cols-2 gap-3">
                  <NumField label="G-Master SMX Outdoor" value={sheet.invest_g_master} onChange={(v) => setSheet({ ...sheet, invest_g_master: v })} testid="cost-g-master" />
                  <NumField label="Intégration TWINT/Payconnect" value={sheet.invest_integration} onChange={(v) => setSheet({ ...sheet, invest_integration: v })} testid="cost-integration" />
                  <NumField label="Lecteur Nayax" value={sheet.invest_nayax_reader} onChange={(v) => setSheet({ ...sheet, invest_nayax_reader: v })} testid="cost-nayax-reader" />
                  <NumField label="Câble MDB Nayax" value={sheet.invest_mdb_cable} onChange={(v) => setSheet({ ...sheet, invest_mdb_cable: v })} testid="cost-mdb-cable" />
                  <NumField label="Transport + install + config" value={sheet.invest_transport_install} onChange={(v) => setSheet({ ...sheet, invest_transport_install: v })} testid="cost-transport" />
                  <NumField label="Autres frais" value={sheet.invest_other} onChange={(v) => setSheet({ ...sheet, invest_other: v })} testid="cost-other" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <NumField label="TVA sur invest (%, ex 8.1)" value={(sheet.invest_tva_rate * 100).toFixed(2)} onChange={(v) => setSheet({ ...sheet, invest_tva_rate: Number(v) / 100 })} testid="cost-invest-tva" />
                  <NumField label="Amortissement (mois)" value={sheet.amort_months} onChange={(v) => setSheet({ ...sheet, amort_months: Math.max(1, parseInt(v || 48, 10)) })} testid="cost-amort-months" integer />
                </div>
                <div className="mt-3 text-xs text-[#575A57]">
                  <b>Total HT :</b> {CHF(
                    (sheet.invest_g_master || 0) + (sheet.invest_integration || 0) +
                    (sheet.invest_nayax_reader || 0) + (sheet.invest_mdb_cable || 0) +
                    (sheet.invest_transport_install || 0) + (sheet.invest_other || 0)
                  )}
                  {" · "}
                  <b>Total TTC :</b> {CHF(
                    ((sheet.invest_g_master || 0) + (sheet.invest_integration || 0) +
                    (sheet.invest_nayax_reader || 0) + (sheet.invest_mdb_cable || 0) +
                    (sheet.invest_transport_install || 0) + (sheet.invest_other || 0)) * (1 + (sheet.invest_tva_rate || 0))
                  )}
                  {" · "}
                  <b>Amort mensuel :</b> {CHF(
                    (((sheet.invest_g_master || 0) + (sheet.invest_integration || 0) +
                    (sheet.invest_nayax_reader || 0) + (sheet.invest_mdb_cable || 0) +
                    (sheet.invest_transport_install || 0) + (sheet.invest_other || 0)) * (1 + (sheet.invest_tva_rate || 0)))
                    / Math.max(1, sheet.amort_months || 48)
                  )}
                </div>
              </fieldset>

              <fieldset className="border border-[#E5E5E0] rounded-lg p-3">
                <legend className="text-xs font-semibold text-[#1F4E3D] px-2">Frais annexes annuels (CHF HT)</legend>
                <div className="grid grid-cols-2 gap-3">
                  <NumField label="Personnalisation marketing" value={sheet.annex_marketing} onChange={(v) => setSheet({ ...sheet, annex_marketing: v })} testid="annex-marketing" />
                  <NumField label="Caméra" value={sheet.annex_camera} onChange={(v) => setSheet({ ...sheet, annex_camera: v })} testid="annex-camera" />
                  <NumField label="Assurance vol / dégât" value={sheet.annex_insurance} onChange={(v) => setSheet({ ...sheet, annex_insurance: v })} testid="annex-insurance" />
                  <NumField label="SIM card" value={sheet.annex_sim} onChange={(v) => setSheet({ ...sheet, annex_sim: v })} testid="annex-sim" />
                  <NumField label="Support Payconnect" value={sheet.annex_payconnect_support} onChange={(v) => setSheet({ ...sheet, annex_payconnect_support: v })} testid="annex-payconnect" />
                  <NumField label="Taxe cantonale" value={sheet.annex_cantonal_tax} onChange={(v) => setSheet({ ...sheet, annex_cantonal_tax: v })} testid="annex-cantonal" />
                  <NumField label="Autres" value={sheet.annex_other} onChange={(v) => setSheet({ ...sheet, annex_other: v })} testid="annex-other" />
                  <NumField label="TVA annexes (%, ex 8.1)" value={(sheet.annex_tva_rate * 100).toFixed(2)} onChange={(v) => setSheet({ ...sheet, annex_tva_rate: Number(v) / 100 })} testid="annex-tva" />
                </div>
                <div className="mt-3 text-xs text-[#575A57]">
                  <b>Total HT/an :</b> {CHF(
                    (sheet.annex_marketing || 0) + (sheet.annex_camera || 0) + (sheet.annex_insurance || 0) +
                    (sheet.annex_sim || 0) + (sheet.annex_payconnect_support || 0) + (sheet.annex_cantonal_tax || 0) + (sheet.annex_other || 0)
                  )}
                  {" · "}
                  <b>Frais mensualisés :</b> {CHF(
                    ((sheet.annex_marketing || 0) + (sheet.annex_camera || 0) + (sheet.annex_insurance || 0) +
                    (sheet.annex_sim || 0) + (sheet.annex_payconnect_support || 0) + (sheet.annex_cantonal_tax || 0) + (sheet.annex_other || 0))
                    * (1 + (sheet.annex_tva_rate || 0)) / 12
                  )}
                </div>
              </fieldset>

              <fieldset className="border border-[#E5E5E0] rounded-lg p-3">
                <legend className="text-xs font-semibold text-[#1F4E3D] px-2">Paramètres calcul</legend>
                <div className="grid grid-cols-2 gap-3">
                  <NumField label="Prix moyen fabrication CHF/barquette" value={sheet.avg_fab_price} onChange={(v) => setSheet({ ...sheet, avg_fab_price: v })} testid="param-avg-fab" />
                  <NumField label="TVA ventes (% denrées, ex 2.6)" value={(sheet.sales_tva_rate * 100).toFixed(2)} onChange={(v) => setSheet({ ...sheet, sales_tva_rate: Number(v) / 100 })} testid="param-sales-tva" />
                  <NumField label="Commission Nayax cartes (%, ex 3.38)" value={(sheet.comm_nayax_card_rate * 100).toFixed(2)} onChange={(v) => setSheet({ ...sheet, comm_nayax_card_rate: Number(v) / 100 })} testid="param-comm-nayax" />
                  <NumField label="Commission Payconnect TWINT (%, ex 1.30)" value={(sheet.comm_payconnect_twint_rate * 100).toFixed(2)} onChange={(v) => setSheet({ ...sheet, comm_payconnect_twint_rate: Number(v) / 100 })} testid="param-comm-payconnect" />
                  <NumField label="Prix fixe par tx TWINT (CHF)" value={sheet.comm_twint_fixed} onChange={(v) => setSheet({ ...sheet, comm_twint_fixed: v })} testid="param-twint-fixed" />
                </div>
              </fieldset>

              <div>
                <Label className="text-xs">Notes</Label>
                <textarea
                  value={sheet.notes || ""}
                  onChange={(e) => setSheet({ ...sheet, notes: e.target.value })}
                  className="w-full border border-[#E5E5E0] rounded p-2 text-sm h-16"
                  data-testid="cost-notes"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenDist(null); setSheet(null); }} data-testid="pc-cost-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving || !sheet} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="pc-cost-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? "…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ============== Tab 2 — Contrats partenaires ==============

function ContractsTab({ distributors, partners, contracts, loading, onChanged, confirm, openContractId }) {
  const [openNew, setOpenNew] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [docCounts, setDocCounts] = useState({});
  const [autoOpened, setAutoOpened] = useState(false);

  useEffect(() => {
    api.get("/contract-documents/summary").then((r) => setDocCounts(r.data || {})).catch(() => {});
  }, [contracts, openNew]);

  useEffect(() => {
    if (autoOpened || !openContractId) return;
    const c = contracts.find((x) => x.id === openContractId);
    if (c) { setAutoOpened(true); setEditContract(c); setForm({ ...c }); setOpenNew(true); }
  }, [contracts, openContractId, autoOpened]);

  const partnerMap = useMemo(() => Object.fromEntries(partners.map((p) => [p.id, p])), [partners]);
  const distMap = useMemo(() => Object.fromEntries(distributors.map((d) => [d.id, d])), [distributors]);

  const openCreate = () => {
    setEditContract(null);
    setForm({
      partner_id: "", distributor_id: "",
      contract_start_date: "",  // Phase 101du.1 : choix libre (pas de valeur par défaut)
      contract_end_date: "",
      ca_threshold: 3000, rate_below_threshold: 0.40, rate_above_threshold: 0.50,
      min_monthly: 50, partner_tva_registered: true, notes: "",
    });
    setOpenNew(true);
  };

  const openEdit = (c) => {
    setEditContract(c);
    setForm({ ...c });
    setOpenNew(true);
  };

  const save = async () => {
    if (!editContract && (!form.partner_id || !form.distributor_id)) {
      toast.error("Sélectionnez un partenaire et une machine");
      return;
    }
    setSaving(true);
    try {
      if (editContract) {
        const payload = {
          partner_id: form.partner_id,
          contract_start_date: form.contract_start_date || null,
          contract_end_date: form.contract_end_date || null,
          notice_period_months: form.notice_period_months ? Number(form.notice_period_months) : null,
          ca_threshold: Number(form.ca_threshold),
          rate_below_threshold: Number(form.rate_below_threshold),
          rate_above_threshold: Number(form.rate_above_threshold),
          min_monthly: Number(form.min_monthly),
          partner_tva_registered: !!form.partner_tva_registered,
          notes: form.notes || null,
        };
        if (!payload.contract_start_date) {
          toast.error("La date de début est obligatoire");
          setSaving(false);
          return;
        }
        if (!payload.partner_id) {
          toast.error("Le partenaire est obligatoire");
          setSaving(false);
          return;
        }
        await api.patch(`/partner-contracts/${editContract.id}`, payload);
        toast.success("Contrat mis à jour");
      } else {
        await api.post(`/partner-contracts`, {
          ...form,
          contract_end_date: form.contract_end_date || null,
          notice_period_months: form.notice_period_months ? Number(form.notice_period_months) : null,
          ca_threshold: Number(form.ca_threshold),
          rate_below_threshold: Number(form.rate_below_threshold),
          rate_above_threshold: Number(form.rate_above_threshold),
          min_monthly: Number(form.min_monthly),
        });
        toast.success("Contrat créé");
      }
      setOpenNew(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const disable = async (c) => {
    const ok = await confirm({
      title: "Désactiver ce contrat ?",
      description: `Contrat ${partnerMap[c.partner_id]?.name} × ${distMap[c.distributor_id]?.name}. Les rapports mensuels futurs ne seront plus calculés. L'historique reste préservé.`,
      confirmText: "Désactiver",
    });
    if (!ok) return;
    try {
      await api.delete(`/partner-contracts/${c.id}`);
      toast.success("Contrat désactivé");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-[#575A57]">
          {contracts.filter((c) => c.active).length} contrat(s) actif(s) · {contracts.filter((c) => !c.active).length} désactivé(s)
        </div>
        <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="pc-contract-new">
          <Plus className="w-4 h-4 mr-1" /> Nouveau contrat
        </Button>
      </div>

      {loading && <div className="text-sm text-[#575A57]">Chargement…</div>}

      <div className="border border-[#E5E5E0] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0]">
            <tr>
              <th className="text-left px-3 py-2">Partenaire</th>
              <th className="text-left px-3 py-2">Machine</th>
              <th className="text-left px-3 py-2">Du</th>
              <th className="text-left px-3 py-2">Au</th>
              <th className="text-center px-3 py-2">Documents</th>
              <th className="text-center px-3 py-2">Palier CA</th>
              <th className="text-center px-3 py-2">Taux &lt; palier</th>
              <th className="text-center px-3 py-2">Taux ≥ palier</th>
              <th className="text-center px-3 py-2">Min. garanti</th>
              <th className="text-center px-3 py-2">Statut</th>
              <th className="text-right px-3 py-2 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={c.id} className={`border-t border-[#E5E5E0] ${!c.active ? "opacity-60" : ""}`} data-testid={`pc-contract-row-${c.id}`}>
                <td className="px-3 py-2">{partnerMap[c.partner_id]?.name || c.partner_id.slice(0, 8)}</td>
                <td className="px-3 py-2">{distMap[c.distributor_id]?.name || c.distributor_id.slice(0, 8)}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.contract_start_date}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {c.contract_end_date || "—"}
                  {noticeDeadline(c.contract_end_date, c.notice_period_months) && (
                    <div className="text-[10px] text-[#A04E2C] font-sans">résilier avant le {noticeDeadline(c.contract_end_date, c.notice_period_months)}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-center" data-testid={`pc-contract-docs-${c.id}`}>
                  {docCounts[c.id] ? <Badge className="bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]"><FileText className="w-3 h-3 mr-1" />{docCounts[c.id]}</Badge> : <span className="text-xs text-[#C05C47]">aucun</span>}
                </td>
                <td className="px-3 py-2 text-center">{CHF(c.ca_threshold)}</td>
                <td className="px-3 py-2 text-center">{(c.rate_below_threshold * 100).toFixed(0)}%</td>
                <td className="px-3 py-2 text-center font-semibold">{(c.rate_above_threshold * 100).toFixed(0)}%</td>
                <td className="px-3 py-2 text-center">{CHF(c.min_monthly)}</td>
                <td className="px-3 py-2 text-center">
                  <Badge className={c.active ? "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]" : "bg-[#F4F4F0] text-[#575A57]"}>
                    {c.active ? "Actif" : "Désactivé"}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`pc-contract-edit-${c.id}`}><Edit3 className="w-3 h-3" /></Button>
                  {c.active && (
                    <Button variant="ghost" size="sm" onClick={() => disable(c)} data-testid={`pc-contract-disable-${c.id}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  )}
                </td>
              </tr>
            ))}
            {contracts.length === 0 && !loading && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-[#575A57]">Aucun contrat encore créé</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editContract ? "Modifier le contrat" : "Nouveau contrat partenaire"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              {/* Partenaire : éditable en création ET en modification (Phase 101dz) */}
              <div>
                <Label className="text-xs">Partenaire hébergeur *</Label>
                <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                  <SelectTrigger data-testid="pc-form-partner"><SelectValue placeholder="Personne / société qui héberge la machine…" /></SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!editContract ? (
                  <div className="text-[10px] text-[#575A57] mt-1">
                    Ce n&apos;est pas Maison Thaï, mais la personne ou l&apos;établissement qui loge physiquement la machine.
                    Créez-en un dans /partenaires si absent de la liste.
                  </div>
                ) : (
                  form.partner_id !== editContract.partner_id && (
                    <div className="text-[10px] text-[#C05C47] mt-1">
                      ⚠ Changement de partenaire hébergeur — les rapports mensuels déjà calculés/finalisés resteront rattachés à ce contrat mais afficheront le nouveau nom (snapshot). Pour un changement de propriétaire en cours de mois, préférez désactiver ce contrat et en créer un nouveau au bon jour.
                    </div>
                  )
                )}
              </div>
              {/* Machine : visible mais non-modifiable en édition (change trop rarement) */}
              {!editContract ? (
                <div>
                  <Label className="text-xs">Machine *</Label>
                  <Select value={form.distributor_id} onValueChange={(v) => setForm({ ...form, distributor_id: v })}>
                    <SelectTrigger data-testid="pc-form-machine"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>
                      {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className="text-xs">Machine</Label>
                  <Input value={distributors.find((d) => d.id === form.distributor_id)?.name || form.distributor_id} disabled data-testid="pc-form-machine-locked" />
                  <div className="text-[10px] text-[#575A57] mt-1 italic">
                    La machine reste liée au contrat. Pour changer de machine, créez un nouveau contrat.
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Début{editContract ? " *" : ""}</Label>
                  <Input type="date" value={form.contract_start_date} onChange={(e) => setForm({ ...form, contract_start_date: e.target.value })} data-testid="pc-form-start" />
                  {editContract && form.contract_start_date !== editContract.contract_start_date && (
                    <div className="text-[10px] text-[#C05C47] mt-1">
                      ⚠ Modifier la date de début rétroactivement peut affecter la cohérence des rapports mensuels déjà calculés/finalisés (contrat initial : {editContract.contract_start_date}).
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Fin (optionnel)</Label>
                  <Input type="date" value={form.contract_end_date || ""} onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })} data-testid="pc-form-end" />
                </div>
                <div className="col-span-2">
                  <NumField label="Préavis de résiliation (mois)" value={form.notice_period_months ?? ""} onChange={(v) => setForm({ ...form, notice_period_months: v })} testid="pc-form-notice" integer />
                  {noticeDeadline(form.contract_end_date, form.notice_period_months) && (
                    <div className="text-[11px] text-[#A04E2C] mt-1" data-testid="pc-form-notice-deadline">
                      À résilier au plus tard le {noticeDeadline(form.contract_end_date, form.notice_period_months)}
                    </div>
                  )}
                </div>
                <NumField label="Palier CA (CHF)" value={form.ca_threshold} onChange={(v) => setForm({ ...form, ca_threshold: v })} testid="pc-form-threshold" />
                <NumField label="Minimum mensuel (CHF)" value={form.min_monthly} onChange={(v) => setForm({ ...form, min_monthly: v })} testid="pc-form-min" />
                <NumField label="Taux < palier (%)" value={(form.rate_below_threshold * 100).toFixed(0)} onChange={(v) => setForm({ ...form, rate_below_threshold: Number(v) / 100 })} testid="pc-form-rate-below" />
                <NumField label="Taux ≥ palier (%)" value={(form.rate_above_threshold * 100).toFixed(0)} onChange={(v) => setForm({ ...form, rate_above_threshold: Number(v) / 100 })} testid="pc-form-rate-above" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.partner_tva_registered}
                  onChange={(e) => setForm({ ...form, partner_tva_registered: e.target.checked })}
                  data-testid="pc-form-tva"
                />
                Partenaire assujetti TVA 8.1% (affiche l&apos;info dans le rapport)
              </label>
              <div>
                <Label className="text-xs">Notes</Label>
                <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-[#E5E5E0] rounded p-2 text-sm h-14" data-testid="pc-form-notes" />
              </div>
              {editContract ? (
                <ContractDocuments contractId={editContract.id} confirm={confirm} />
              ) : (
                <div className="text-[11px] text-[#575A57]">Enregistrez d&apos;abord le contrat, puis rouvrez-le pour joindre le document signé.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)} data-testid="pc-form-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="pc-form-save">
              {saving ? "…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ============== Tab 3 — Rapports mensuels ==============

function ReportsTab({ distributors, partners, contracts, reports, loadReports, confirm }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);  // JS month 0-indexed
  const [computing, setComputing] = useState(false);
  const [manualByContract, setManualByContract] = useState({});  // {contractId: {enable, nb_sales, ca_brut, ca_cards, ca_twint, nb_tx_twint, encash}}
  const [payingReport, setPayingReport] = useState(null);  // rapport en cours de "marquer payé"
  const [emailingReport, setEmailingReport] = useState(null);  // rapport en cours d'envoi email
  const [encashReport, setEncashReport] = useState(null);  // Phase 101dz — rapport en cours d'édition encash
  const partnerMap = useMemo(() => Object.fromEntries(partners.map((p) => [p.id, p])), [partners]);
  const distMap = useMemo(() => Object.fromEntries(distributors.map((d) => [d.id, d])), [distributors]);

  useEffect(() => {
    loadReports(year, month);
  }, [year, month, loadReports]);

  const buildComputePayload = (contractId) => {
    const m = manualByContract[contractId] || {};
    const payload = { partner_direct_encash: Number(m.encash || 0) };
    if (m.enable) {
      payload.manual_sales_data = {
        nb_sales: parseInt(m.nb_sales || 0, 10),
        ca_brut: Number(m.ca_brut || 0),
        ca_cards: Number(m.ca_cards || 0),
        ca_twint: Number(m.ca_twint || 0),
        nb_tx_twint: parseInt(m.nb_tx_twint || 0, 10),
      };
    }
    return payload;
  };

  const prefillFromNayax = async (c) => {
    try {
      const { data } = await api.get(`/statistics/nayax-prefill/commission?distributor_id=${c.distributor_id}&year=${year}&month=${month}`);
      if (!data.found) {
        toast.error("Aucun récap Nayax importé pour ce mois (Statistiques → Ventes par mois → Importer un récap Nayax)");
        return;
      }
      setManualByContract((prev) => ({
        ...prev,
        [c.id]: {
          ...(prev[c.id] || {}), enable: true,
          nb_sales: data.nb_sales ?? "", ca_brut: data.ca_brut ?? "", ca_cards: data.ca_cards ?? "",
          ca_twint: data.ca_twint ?? "", nb_tx_twint: data.nb_tx_twint ?? "",
        },
      }));
      toast.success(`Chiffres Nayax ${data.period} repris : vérifiez puis calculez`);
      (data.warnings || []).forEach((w) => toast.warning(w));
      if (data.existing_report?.finalized) {
        toast.error("Un rapport finalisé existe déjà pour ce mois : il ne sera pas recalculé tant qu'il reste finalisé.");
      } else if (data.existing_report?.payment_date) {
        toast.warning(`Rapport déjà marqué payé le ${data.existing_report.payment_date} : à vérifier avant recalcul.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Préremplissage impossible");
    }
  };

  const computeAll = async () => {
    setComputing(true);
    let ok = 0, ko = 0;
    for (const c of contracts) {
      try {
        await api.post(
          `/commission-reports/compute?distributor_id=${c.distributor_id}&year=${year}&month=${month}`,
          buildComputePayload(c.id)
        );
        ok++;
      } catch {
        ko++;
      }
    }
    toast.success(`${ok} rapport(s) calculé(s)${ko > 0 ? ` · ${ko} échec(s)` : ""}`);
    setComputing(false);
    loadReports(year, month);
  };

  const computeOne = async (c) => {
    try {
      await api.post(
        `/commission-reports/compute?distributor_id=${c.distributor_id}&year=${year}&month=${month}`,
        buildComputePayload(c.id)
      );
      toast.success(`Rapport ${distMap[c.distributor_id]?.name} calculé`);
      loadReports(year, month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur calcul");
    }
  };

  const finalize = async (r) => {
    const ok = await confirm({
      title: "Finaliser ce rapport ?",
      description: `Le rapport ${MONTHS_FR[r.period_month - 1]} ${r.period_year} sera verrouillé. Impossible de le recalculer sans le dé-finaliser.`,
      confirmText: "Finaliser",
    });
    if (!ok) return;
    try {
      await api.post(`/commission-reports/${r.id}/finalize`);
      toast.success("Rapport finalisé");
      loadReports(year, month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: "Supprimer ce rapport ?",
      description: "Impossible à annuler. Il faudra recalculer.",
      confirmText: "Supprimer",
    });
    if (!ok) return;
    try {
      await api.delete(`/commission-reports/${r.id}`);
      toast.success("Rapport supprimé");
      loadReports(year, month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const setManualField = (contractId, field, value) => {
    setManualByContract((prev) => ({
      ...prev,
      [contractId]: { ...(prev[contractId] || {}), [field]: value },
    }));
  };

  return (
    <>
      <div className="flex items-end gap-3 flex-wrap mb-4">
        <div>
          <Label className="text-xs">Année</Label>
          <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value || now.getFullYear(), 10))} className="w-24" data-testid="pc-report-year" />
        </div>
        <div>
          <Label className="text-xs">Mois</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
            <SelectTrigger className="w-36" data-testid="pc-report-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS_FR.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <BatchNayaxComputeDialog
          contracts={contracts}
          labelOf={(c) => `${partnerMap[c.partner_id]?.name || "?"} · ${distMap[c.distributor_id]?.name || "?"}`}
          onDone={() => loadReports(year, month)}
        />
        <Button onClick={computeAll} disabled={computing || contracts.length === 0} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="pc-compute-all">
          <RefreshCw className={`w-4 h-4 mr-1 ${computing ? "animate-spin" : ""}`} /> Calculer tous les rapports {MONTHS_FR[month - 1]} {year}
        </Button>
      </div>

      {contracts.length === 0 && (
        <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded p-3 text-sm text-[#8C6B2F] mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> Aucun contrat actif — créez d&apos;abord un contrat partenaire dans l&apos;onglet précédent.
        </div>
      )}

      {contracts.length > 0 && (
        <div className="border border-[#E5E5E0] rounded-lg bg-white p-3 mb-3 space-y-3">
          <div className="text-xs font-semibold text-[#1F4E3D]">Saisie mensuelle par contrat</div>
          <div className="text-[10px] text-[#575A57]">
            Sans saisie manuelle, le CA du mois est lu automatiquement depuis les ventes Nayax synchronisées.
            Cochez « Saisie manuelle » pour saisir les valeurs à la main (ex. avant synchro Nayax complète).
          </div>
          {contracts.map((c) => {
            const m = manualByContract[c.id] || {};
            return (
              <div key={c.id} className="border border-[#E5E5E0] rounded p-2 space-y-2" data-testid={`pc-manual-block-${c.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-medium text-sm text-[#1A1C1A]">
                    {partnerMap[c.partner_id]?.name} · <span className="text-[#575A57]">{distMap[c.distributor_id]?.name}</span>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" onClick={() => prefillFromNayax(c)} data-testid={`pc-prefill-nayax-${c.id}`}>
                    Préremplir depuis Nayax
                  </Button>
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={!!m.enable}
                      onChange={(e) => setManualField(c.id, "enable", e.target.checked)}
                      data-testid={`pc-manual-toggle-${c.id}`}
                    />
                    Saisie manuelle CA du mois
                  </label>
                </div>
                {m.enable && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <NumField label="Nb ventes" value={m.nb_sales || ""} onChange={(v) => setManualField(c.id, "nb_sales", v)} testid={`pc-manual-nb-${c.id}`} integer />
                    <NumField label="CA brut (CHF)" value={m.ca_brut || ""} onChange={(v) => setManualField(c.id, "ca_brut", v)} testid={`pc-manual-ca-${c.id}`} />
                    <NumField label="CA cartes" value={m.ca_cards || ""} onChange={(v) => setManualField(c.id, "ca_cards", v)} testid={`pc-manual-cards-${c.id}`} />
                    <NumField label="CA TWINT" value={m.ca_twint || ""} onChange={(v) => setManualField(c.id, "ca_twint", v)} testid={`pc-manual-twint-${c.id}`} />
                    <NumField label="Nb tx TWINT" value={m.nb_tx_twint || ""} onChange={(v) => setManualField(c.id, "nb_tx_twint", v)} testid={`pc-manual-txtwint-${c.id}`} integer />
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-[10px]">Encaissements directs partenaire (cas A dépannage)</Label>
                    <Input
                      type="number" step="0.01"
                      value={m.encash || ""}
                      onChange={(e) => setManualField(c.id, "encash", e.target.value)}
                      placeholder="0.00"
                      className="h-8"
                      data-testid={`pc-encash-${c.id}`}
                    />
                  </div>
                  <Button size="sm" onClick={() => computeOne(c)} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid={`pc-compute-${c.id}`}>
                    <Calculator className="w-3 h-3 mr-1" /> Calculer
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        {reports.length === 0 && (
          <div className="bg-[#F0F5FB] border border-[#C3D1E8] rounded p-3 text-sm text-[#2F5A8C]">
            Aucun rapport pour {MONTHS_FR[month - 1]} {year}. Cliquez « Calculer » pour générer.
          </div>
        )}
        {reports.map((r) => (
          <ReportCard
            key={r.id}
            report={r}
            partnerName={partnerMap[r.partner_id]?.name}
            distName={distMap[r.distributor_id]?.name}
            onFinalize={() => finalize(r)}
            onDelete={() => remove(r)}
            onOpenPayment={() => setPayingReport(r)}
            onOpenEmail={() => setEmailingReport(r)}
            onOpenEncash={() => setEncashReport(r)}
          />
        ))}
      </div>

      <PaymentDialog
        report={payingReport}
        onClose={() => setPayingReport(null)}
        onSaved={() => { setPayingReport(null); loadReports(year, month); }}
      />

      <EmailReportDialog
        report={emailingReport}
        partnerEmail={emailingReport ? (partners.find((p) => p.id === emailingReport.partner_id)?.email || "") : ""}
        onClose={() => setEmailingReport(null)}
      />

      <DirectEncashDialog
        report={encashReport}
        onClose={() => setEncashReport(null)}
        onSaved={() => { setEncashReport(null); loadReports(year, month); }}
      />
    </>
  );
}

function QualityIndicators({ report }) {
  // Phase 101eb — Bloc éditable "Indicateurs qualité" (informatif, aucun impact CHF).
  // Les 2 premiers champs sont saisis par l'équipe AM. Le 3ᵉ est calculé auto.
  // Phase 101ed — Édition permise même sur rapport finalisé/payé (informatif).
  const [editing, setEditing] = useState(false);
  const [blocking, setBlocking] = useState(report.blocking_interventions || 0);
  const [missing, setMissing] = useState(report.missing_barquettes || 0);
  const [saving, setSaving] = useState(false);
  const dlc = report.dlc_expired_barquettes || 0;

  useEffect(() => {
    setBlocking(report.blocking_interventions || 0);
    setMissing(report.missing_barquettes || 0);
  }, [report.id, report.blocking_interventions, report.missing_barquettes]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/commission-reports/${report.id}/info-fields`, {
        blocking_interventions: Number(blocking) || 0,
        missing_barquettes: Number(missing) || 0,
      });
      toast.success("Indicateurs enregistrés");
      setEditing(false);
      // Mutate local pour refléter — le parent va relire au prochain refresh
      report.blocking_interventions = Number(blocking) || 0;
      report.missing_barquettes = Number(missing) || 0;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Phase 101ee — Rafraîchir uniquement le champ DLC (autorisé même sur rapport finalisé)
  const [refreshing, setRefreshing] = useState(false);
  const refreshDlc = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/commission-reports/${report.id}/refresh-dlc`);
      const newVal = data.dlc_expired_barquettes || 0;
      report.dlc_expired_barquettes = newVal;
      if (newVal !== dlc) {
        toast.success(`DLC mise à jour : ${dlc} → ${newVal}`);
      } else {
        toast.info(`DLC déjà à jour (${newVal})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur rafraîchissement DLC");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="border-t border-[#E5E5E0] pt-2 mt-1" data-testid={`pc-quality-${report.id}`}>
      <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#575A57]">
          Indicateurs qualité <span className="italic normal-case font-normal">(informatif — aucun impact CHF)</span>
        </div>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-[10px] text-[#1F4E3D] hover:underline"
            data-testid={`pc-quality-edit-${report.id}`}
          >
            <Pencil className="w-3 h-3 inline mr-0.5" /> Modifier
          </button>
        )}
        {editing && (
          <div className="flex gap-1">
            <button
              onClick={() => { setEditing(false); setBlocking(report.blocking_interventions || 0); setMissing(report.missing_barquettes || 0); }}
              className="text-[10px] px-2 py-0.5 border border-[#E5E5E0] rounded hover:bg-[#F5F5F0]"
              disabled={saving}
              data-testid={`pc-quality-cancel-${report.id}`}
            >
              Annuler
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="text-[10px] px-2 py-0.5 bg-[#1F4E3D] text-white rounded hover:bg-[#173B2E] disabled:opacity-50"
              data-testid={`pc-quality-save-${report.id}`}
            >
              {saving ? "…" : "Enregistrer"}
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-2">
          <div className="text-[9px] uppercase text-[#6B6E6B]">Interventions blocage</div>
          {editing ? (
            <input
              type="number"
              min="0"
              value={blocking}
              onChange={(e) => setBlocking(e.target.value)}
              className="w-full mt-0.5 text-lg font-semibold bg-white border border-[#1F4E3D] rounded px-1 py-0.5"
              data-testid={`pc-quality-blocking-input-${report.id}`}
            />
          ) : (
            <div className="text-lg font-semibold text-[#1A1C1A]" data-testid={`pc-quality-blocking-${report.id}`}>
              {report.blocking_interventions || 0}
            </div>
          )}
        </div>
        <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-2">
          <div className="text-[9px] uppercase text-[#6B6E6B]">Barquettes manquantes</div>
          {editing ? (
            <input
              type="number"
              min="0"
              value={missing}
              onChange={(e) => setMissing(e.target.value)}
              className="w-full mt-0.5 text-lg font-semibold bg-white border border-[#1F4E3D] rounded px-1 py-0.5"
              data-testid={`pc-quality-missing-input-${report.id}`}
            />
          ) : (
            <div className="text-lg font-semibold text-[#1A1C1A]" data-testid={`pc-quality-missing-${report.id}`}>
              {report.missing_barquettes || 0}
            </div>
          )}
        </div>
        <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-2">
          <div className="text-[9px] uppercase text-[#6B6E6B] flex items-center justify-between gap-1">
            <span>Barquettes DLC retirées <span className="italic normal-case">(auto)</span></span>
            <button
              onClick={refreshDlc}
              disabled={refreshing}
              className="text-[9px] text-[#1F4E3D] hover:underline disabled:opacity-50"
              title="Recalcule uniquement la valeur DLC depuis les tournées du mois"
              data-testid={`pc-quality-refresh-dlc-${report.id}`}
            >
              <RefreshCw className={`w-2.5 h-2.5 inline ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "…" : "Rafraîchir"}
            </button>
          </div>
          <div className="text-lg font-semibold text-[#1A1C1A]" data-testid={`pc-quality-dlc-${report.id}`}>
            {dlc}
          </div>
        </div>
      </div>
    </div>
  );
}


function ReportCard({ report, partnerName, distName, onFinalize, onDelete, onOpenPayment, onOpenEmail, onOpenEncash }) {
  const r = report;
  const addressLine = [r.partner_snapshot_address, r.partner_snapshot_postal_code, r.partner_snapshot_city]
    .filter(Boolean).join(" · ");
  const isManual = r.ca_source === "manual";
  const isPaid = !!r.payment_date;
  return (
    <Card className={r.finalized ? "border-[#5A8F76] bg-[#EAF4EE]" : ""} data-testid={`pc-report-${r.id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-semibold text-[#1A1C1A] flex items-center gap-2 flex-wrap">
              {partnerName || r.partner_snapshot_name || "?"}
              <span className="text-[#575A57] font-normal">·</span>
              {distName || "?"}
              {isManual && <Badge className="bg-[#F0E4C3] text-[#8C6B2F] border-[#D4B676] text-[10px]" data-testid={`pc-badge-manual-${r.id}`}>CA saisi manuellement</Badge>}
            </div>
            {addressLine && (
              <div className="text-[11px] text-[#575A57] italic">{addressLine}</div>
            )}
            <div className="text-xs text-[#575A57]">
              {MONTHS_FR[r.period_month - 1]} {r.period_year} · {r.nb_sales} vente(s) · CA brut {CHF(r.ca_brut)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/commission-reports/${r.id}/pdf`, "_blank")} data-testid={`pc-pdf-${r.id}`}>
              <Download className="w-3 h-3 mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={onOpenEmail} data-testid={`pc-email-${r.id}`}>
              <Mail className="w-3 h-3 mr-1" /> Envoyer
            </Button>
            {isPaid ? (
              <Badge className="bg-[#EAF4EE] text-[#1F4E3D] border-[#5A8F76]" data-testid={`pc-badge-paid-${r.id}`}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Payé le {r.payment_date}
                {r.payment_reference && <span className="ml-1 opacity-70">· {r.payment_reference}</span>}
              </Badge>
            ) : (
              <Button size="sm" variant="outline" onClick={onOpenPayment} data-testid={`pc-mark-paid-${r.id}`}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Marquer payé
              </Button>
            )}
            {r.finalized ? (
              <Badge className="bg-[#5A8F76] text-white"><Lock className="w-3 h-3 mr-1" />Finalisé</Badge>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={onFinalize} data-testid={`pc-finalize-${r.id}`}>
                  <Lock className="w-3 h-3 mr-1" />Finaliser
                </Button>
                <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`pc-delete-${r.id}`}>
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <Kpi label="Coût matière" value={CHF(r.matter_cost)} />
          <Kpi label="Commissions" value={CHF((r.comm_nayax || 0) + (r.comm_payconnect || 0) + (r.twint_fixed_cost || 0))} />
          <Kpi label="TVA ventes" value={CHF(r.sales_tva)} />
          <Kpi label="Amort + annexes" value={CHF((r.monthly_amortization || 0) + (r.monthly_annex_costs || 0))} />
          <Kpi label="Total coûts" value={CHF(r.total_costs)} accent="danger" />
          <Kpi label="Bénéfice net" value={CHF(r.net_profit)} accent={r.net_profit >= 0 ? "success" : "danger"} />
          <Kpi label={`Taux ${(r.rate_applied * 100).toFixed(0)}%`} value={CHF(r.partner_rate_gross)} />
          <Kpi
            label={r.partner_direct_encash > 0 ? `− encash ${CHF(r.partner_direct_encash)}` : "Rétribution finale"}
            value={CHF(r.partner_rate_final)}
            accent="highlight"
          />
        </div>
        {/* Phase 101dz — Ligne éditable pour saisir les encaissements directs du partenaire */}
        <div className="flex items-center justify-between text-[11px] border-t border-[#E5E5E0] pt-1.5" data-testid={`pc-encash-row-${r.id}`}>
          <div className="text-[#575A57]">
            <span className="font-medium text-[#1A1C1A]">Encaissements directs partenaire</span>
            <span className="italic"> — montants que le partenaire encaisse lui-même (dépannage, ventes cash spéciales, etc.) et qui sont déduits de la rétribution finale.</span>
          </div>
          <div className="flex items-center gap-2 ml-3">
            <span className="font-mono text-[#1A1C1A]" data-testid={`pc-encash-value-${r.id}`}>
              {CHF(r.partner_direct_encash || 0)}
            </span>
            {r.finalized ? (
              <Badge className="bg-[#F4F4F0] text-[#575A57] text-[9px] px-1.5">Verrouillé</Badge>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                onClick={onOpenEncash}
                className="h-6 px-2 text-[10px]"
                data-testid={`pc-encash-edit-${r.id}`}
              >
                <Pencil className="w-3 h-3 mr-1" /> {r.partner_direct_encash > 0 ? "Modifier" : "Saisir"}
              </Button>
            )}
          </div>
        </div>
        {r.partner_tva_info > 0 && (
          <div className="text-[10px] italic text-[#575A57]">
            ℹ️ Info TVA 8.1% à charge partenaire (services) : {CHF(r.partner_tva_info)}
          </div>
        )}
        {/* Phase 101eb — Indicateurs qualité informatifs (aucun impact CHF) */}
        <QualityIndicators report={r} />
        {r.last_email_sent_at && (
          <div className="text-[10px] text-[#5A8F76] border-t border-[#E5E5E0] pt-1">
            📧 Envoyé à {r.last_email_sent_to} le {new Date(r.last_email_sent_at).toLocaleString("fr-CH")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function EmailReportDialog({ report, partnerEmail, onClose }) {
  const [toEmail, setToEmail] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (report) {
      setToEmail(partnerEmail || "");
      setCustomMessage("");
    }
  }, [report, partnerEmail]);

  const send = async () => {
    if (!toEmail || !toEmail.includes("@")) { toast.error("Email destinataire invalide"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/commission-reports/${report.id}/email`, {
        to_email: toEmail,
        custom_message: customMessage || null,
      });
      toast.success(`Email envoyé à ${data.to}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur envoi email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!report} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Envoyer le rapport par email</DialogTitle>
          <DialogDescription>
            {report && `${report.partner_snapshot_name || ""} · ${MONTHS_FR[report.period_month - 1]} ${report.period_year} · Rétribution ${CHF(report.partner_rate_final)}`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Destinataire *</Label>
            <Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="partenaire@exemple.ch" data-testid="pc-email-to" />
            {!partnerEmail && (
              <div className="text-[10px] text-[#C05C47] mt-1">
                ⚠ Pas d&apos;email dans le fiche partenaire — renseignez-le dans /partenaires ou saisissez ici manuellement.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Message personnalisé (optionnel)</Label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              className="w-full border border-[#E5E5E0] rounded p-2 text-sm h-24"
              placeholder="Ex. Cher partenaire, ci-joint votre décompte de mai. Le virement partira ce vendredi. Bonne journée !"
              data-testid="pc-email-message"
            />
          </div>
          <div className="text-[11px] text-[#575A57] bg-[#F0F5FB] border border-[#C3D1E8] rounded p-2">
            Le PDF sera généré et joint automatiquement. Le corps du mail affiche déjà le montant à verser en gros. Répondre à ce mail arrive dans info@maison-thai.ch.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={send} disabled={sending} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="pc-email-send">
            <Mail className="w-4 h-4 mr-1" /> {sending ? "Envoi…" : "Envoyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DirectEncashDialog({ report, onClose, onSaved }) {
  // Phase 101dz (Feb 2026) — Édition inline du montant `partner_direct_encash`
  // sur un rapport calculé, sans repasser par le formulaire de calcul complet.
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (report) {
      setAmount(String(report.partner_direct_encash ?? 0));
    }
  }, [report]);

  if (!report) return null;

  const parsed = Number(amount);
  const isValid = !isNaN(parsed) && parsed >= 0;
  const gross = Number(report.partner_rate_gross || 0);
  // Preview : on retient max(gross, min_monthly) mais on n'a pas min_monthly côté
  // rapport ; on retient max(gross, current_final + current_encash) qui donne
  // la même base (car final = max(gross, min) − encash).
  const currentBase = Number(report.partner_rate_final || 0) + Number(report.partner_direct_encash || 0);
  const newFinal = isValid ? Math.max(gross, currentBase) - parsed : Number(report.partner_rate_final || 0);

  const save = async () => {
    if (!isValid) { toast.error("Montant invalide (nombre ≥ 0)"); return; }
    setSaving(true);
    try {
      await api.patch(`/commission-reports/${report.id}/direct-encash`, {
        partner_direct_encash: parsed,
      });
      toast.success("Encaissement direct enregistré");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!report} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="pc-encash-dialog">
        <DialogHeader>
          <DialogTitle>Encaissements directs partenaire</DialogTitle>
          <DialogDescription>
            {`${report.partner_snapshot_name || ""} · ${MONTHS_FR[report.period_month - 1]} ${report.period_year}`}
            <br />
            Montants encaissés directement par le partenaire (dépannage caisse, ventes cash particulières, etc.) — <b>déduits</b> de la rétribution finale.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Montant total encaissé (CHF) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="pc-encash-input"
              autoFocus
            />
          </div>
          <div className="bg-[#F0F5FB] border border-[#C3D1E8] rounded p-2 text-[11px] space-y-1">
            <div className="flex justify-between">
              <span className="text-[#575A57]">Rétribution brute (avant déduction)</span>
              <span className="font-mono">{CHF(currentBase)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#C05C47]">− Encaissements directs</span>
              <span className="font-mono text-[#C05C47]">− {CHF(parsed || 0)}</span>
            </div>
            <div className="flex justify-between border-t border-[#C3D1E8] pt-1 font-semibold">
              <span>= Rétribution finale à verser</span>
              <span className="font-mono text-[#8C6B2F]" data-testid="pc-encash-preview">{CHF(newFinal)}</span>
            </div>
          </div>
          <div className="text-[10px] text-[#575A57] italic">
            Ex. : dépannage 22/02 pour 50 CHF encaissés en cash par le partenaire → saisir 50.00.
            Ce montant est déduit du virement mensuel Maison Thaï → partenaire.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={save}
            disabled={saving || !isValid}
            className="bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid="pc-encash-save"
          >
            {saving ? "…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PaymentDialog({ report, onClose, onSaved }) {
  const [date, setDate] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (report) {
      setDate(report.payment_date || new Date().toISOString().slice(0, 10));
      setReference(report.payment_reference || "");
      setNotes(report.payment_notes || "");
    }
  }, [report]);

  const save = async () => {
    if (!date) { toast.error("Date obligatoire"); return; }
    setSaving(true);
    try {
      await api.post(`/commission-reports/${report.id}/payment`, {
        payment_date: date,
        payment_reference: reference || null,
        payment_notes: notes || null,
      });
      toast.success("Paiement enregistré");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!report} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Marquer comme payé</DialogTitle>
          <DialogDescription>
            {report && `${report.partner_snapshot_name || ""} · ${MONTHS_FR[report.period_month - 1]} ${report.period_year} · ${CHF(report.partner_rate_final)}`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Date de paiement *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="pc-payment-date" />
          </div>
          <div>
            <Label className="text-xs">Référence (n° virement, TWINT, cash…)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="ex. VIR-2026-05-14" data-testid="pc-payment-ref" />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-[#E5E5E0] rounded p-2 text-sm h-16" data-testid="pc-payment-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="pc-payment-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, accent }) {
  const color = accent === "success" ? "text-[#1F4E3D]"
    : accent === "danger" ? "text-[#C05C47]"
    : accent === "highlight" ? "text-[#8C6B2F] font-bold text-sm"
    : "text-[#1A1C1A]";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">{label}</div>
      <div className={`font-mono ${color}`}>{value}</div>
    </div>
  );
}


// ============== Composant utilitaire : champ numérique ==============

function NumField({ label, value, onChange, testid, integer = false }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        step={integer ? "1" : "0.01"}
        value={value ?? 0}
        onChange={(e) => onChange(integer ? parseInt(e.target.value || 0, 10) : Number(e.target.value || 0))}
        data-testid={testid}
      />
    </div>
  );
}
